import Text from "common/Typography/Text";
import React, { ChangeEvent, useEffect, useRef } from "react";
import { FixMeLater } from "types";
import { ClientData, ConfigurationExchangeInfo } from "@shared/types";
import Flex from "common/Boxes/Flex";
import styled, { useTheme } from "styled-components";
import Title from "common/Typography/Title";
import useSelectSubExchange from "@shared/features/portfolio/createAccount/useSelectSubExchange";
import Input from "common/Inputs/Input";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "@shared/common/constants";
import RequiredFieldMessage from "common/Animations/RequiredFieldMessage";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { useBottomNavigator } from "utils/hooks/misc";
import { BOTTOM_NAVIGATOR } from "common/constants";
import Checkbox from "common/Checkbox/Checkbox";

interface SelectSubExchangeProps {
  primaryExchange?: string;
  isEdit?: boolean;
  account?: ClientData;
  setFormValue?: (fieldName: string, value: string) => void;
}

const SelectSubExchange = ({
  primaryExchange,
  account,
  isEdit,
}: SelectSubExchangeProps) => {
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const bottomNavigatorData = useBottomNavigator();
  const {
    subExchanges,
    getCheckboxData,
    accountNameInputData,
    accountNameInitialized,
  } = useSelectSubExchange({
    primaryExchange,
    account,
    isEdit,
  });

  useEffect(() => {
    if (!bottomNavigatorData.baseShouldDisplay) {
      inputRef?.current?.focus({ preventScroll: true });
    }
  }, [inputRef]);

  const accountNameInput = (
    <div style={{ marginTop: isEdit ? 0 : 24, marginBottom: 24 }}>
      <InputLabel>{accountNameInputData.label}</InputLabel>
      <Input
        ref={inputRef as FixMeLater}
        value={accountNameInputData.value}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          accountNameInputData.onChange(e.target.value)
        }
      />
      <RequiredFieldMessage
        requirement={!!accountNameInputData.error}
        errorMessage={accountNameInputData.error || ""}
        style={{ marginTop: 4 }}
      />
    </div>
  );

  const getCheckbox = (subExchange: ConfigurationExchangeInfo) => {
    const checkboxData = getCheckboxData(subExchange);
    return (
      <StyledCheckbox
        key={checkboxData.key}
        value={checkboxData.value}
        checked={checkboxData.checked}
        onChange={checkboxData.onChange}
        $checked={checkboxData.checked}
      >
        <Title
          level={4}
          type={checkboxData.exchangeError ? "danger" : "primary"}
        >
          {checkboxData.exchange}
          {checkboxData.account && (
            <AccountName $error={checkboxData.accountError}>
              {checkboxData.account}
            </AccountName>
          )}
        </Title>
        <Description $isMobile={isMobile}>
          {checkboxData.description}
        </Description>
      </StyledCheckbox>
    );
  };

  return (
    <LoadingSpinnerOverlay
      isLoading={!accountNameInitialized}
      backgroundColor={theme.colors.layoutBgColor}
      spinnerMarginTop={100}
    >
      {!isEdit && (
        <div>
          <StyledText>
            Which of the sub-exchanges you would like to connect?
          </StyledText>
          <SubExchangesWrapper>
            {subExchanges?.map((subExchange) => getCheckbox(subExchange))}
          </SubExchangesWrapper>
        </div>
      )}
      {accountNameInputData.shouldDisplay && accountNameInput}
    </LoadingSpinnerOverlay>
  );
};

export default SelectSubExchange;

const StyledText = styled(Text)`
  margin-bottom: 12px;
`;
const SubExchangesWrapper = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 20px;
`;
const StyledCheckbox = styled(Checkbox)<{ $checked?: boolean }>`
  .ant-checkbox {
    align-self: flex-start;
    top: 3px;
  }
  margin-right: 4px;
  padding: 12px;
  border-radius: 4px;
  width: 100%;
  ${(p) =>
    p.$checked &&
    `
    background: ${
      p.theme.isDarkMode
        ? p.theme.colors.cardLightBgColor
        : p.theme.colors.statisticsBgColor
    };
  `};
  @media (min-width: ${BOTTOM_NAVIGATOR}) {
    &:hover {
      background: ${(p) =>
        p.theme.isDarkMode
          ? p.theme.colors.cardLightBgColor
          : p.theme.colors.statisticsBgColor};
    }
  }
`;
const Description = styled(Text)<{ $isMobile?: boolean }>`
  color: ${(p) => p.theme.colors.mediumGrey};
  font-size: ${(p) =>
    p.$isMobile
      ? p.theme.typography.fontSizes.S
      : p.theme.typography.fontSizes.M};
`;
const AccountName = styled.span<{ $error: boolean }>`
  margin-left: 4px;
  color: ${(p) =>
    p.$error ? p.theme.colors.red : p.theme.colors.secondaryGrey};
`;
const InputLabel = styled(Text)`
  padding-bottom: 8px;
  color: ${(p) => p.theme.colors.basic};
`;
